import {
  AdaptedPageItem,
  InterfaceKeys,
  SettingKeys,
  SettingsState,
  ThemeType,
} from '../contracts/Settings'

import { Registry } from '../helpers/registry'
import { HTTP_REST_SERVER } from '@/modules/axios'

const getSiteDataObj = (state: SettingsState, key: SettingKeys): any =>
  state.siteData[state.curLang] ? state.siteData[state.curLang][key] : {}

const getSiteDataInterface = (
  state: SettingsState,
  key: InterfaceKeys
): string =>
  state.siteData[state.curLang] && state.siteData[state.curLang].interfaceText
    ? state.siteData[state.curLang].interfaceText[key]
    : ''

export const state = (): SettingsState => {
  return {
    siteData: {},
    curLang: 'ru',
    hideFooter: false,
    hideHeader: false,
    hideSurveyBtn: false,
    showSurveyPopup: false,
    theme: ThemeType.primary,
    isMapInited: false,
    iframeSrcs: [],
    notification: undefined,
    featureFlags: {},
    userLocation: {
      city: '',
      region: '',
      region_with_type: '',
    },
    mobileAppBanner: {},
    showFloatingMenuButton: true,
    headerVisuallyHidden: false,
    headerFullWidth: false,
    headerFooterNewDesign: false,
    isProstoArticle: false,
  }
}

export const getters = {
  getMobileAppBanner: (state: SettingsState) =>
    getSiteDataObj(state, 'mobileAppBanner'),
  getHideFooter: (state: SettingsState) => state.hideFooter,
  getHideHeader: (state: SettingsState) => state.hideHeader,
  getHideSurveyBtn: (state: SettingsState) => state.hideSurveyBtn,
  getSurveyButtonNewDesign: (state: SettingsState) =>
    state.surveyButtonNewDesign,
  getMapIsInit: (state: SettingsState) => state.isMapInited,
  getIframeSrcs: (state: SettingsState) => state.iframeSrcs,
  getFeatureFlags: (state: SettingsState) => state.featureFlags,
  uniquePageUrls: (state: SettingsState) =>
    getSiteDataObj(state, 'uniquePageUrls'),
  seo: (state: SettingsState) => getSiteDataObj(state, 'seo'),
  mainSectionCode: (state: SettingsState) =>
    getSiteDataObj(state, 'mainSectionCode'),
  footerInfo: (state: SettingsState) => getSiteDataObj(state, 'footerInfo'),
  headerInfo: (state: SettingsState) => getSiteDataObj(state, 'headerInfo'),
  navigation: (state: SettingsState) => getSiteDataObj(state, 'navigation'),
  popupBuyOnline: (state: SettingsState): string =>
    getSiteDataObj(state, 'popupBuyOnline'),
  btnRead: (state: SettingsState): string =>
    getSiteDataInterface(state, 'btnRead'),
  btnGo: (state: SettingsState): string => getSiteDataInterface(state, 'btnGo'),
  btnShowMore: (state: SettingsState): string =>
    getSiteDataInterface(state, 'btnShowMore'),
  btnRollUp: (state: SettingsState): string =>
    getSiteDataInterface(state, 'btnRollUp'),
  btnFormAgain: (state: SettingsState): string =>
    getSiteDataInterface(state, 'btnFormAgain'),
  textFailedForm: (state: SettingsState): string =>
    getSiteDataInterface(state, 'textFailedForm'),
  btnLoadMore: (state: SettingsState): string =>
    getSiteDataInterface(state, 'btnLoadMore'),
  oldBrowserTitle: (state: SettingsState): string =>
    getSiteDataInterface(state, 'oldBrowserTitle'),
  oldBrowserDescription: (state: SettingsState): string =>
    getSiteDataInterface(state, 'oldBrowserDescription'),
  oldBrowserLinkTitle: (state: SettingsState): string =>
    getSiteDataInterface(state, 'oldBrowserLinkTitle'),
  oldBrowserLinkUrl: (state: SettingsState): string =>
    getSiteDataInterface(state, 'oldBrowserLinkUrl'),
  cookie: (state: SettingsState): string => getSiteDataObj(state, 'cookie'),
  curLang: (state: SettingsState): string => state.curLang,
  survey: (state: SettingsState) => getSiteDataObj(state, 'survey'),
  notification: (state: SettingsState) => state.notification,
  holidayTheme: (state: SettingsState) => getSiteDataObj(state, 'holidayTheme'),
  allSiteNotification: (state: SettingsState) =>
    getSiteDataObj(state, 'allSiteNotification'),
  getAuthModalData: (state: SettingsState) =>
    getSiteDataObj(state, 'authModal'),
  getShowSurveyPopup: (state: SettingsState) => state.showSurveyPopup,
  getSeoCityList: (state: SettingsState) => state.siteData.ru.seoCityList,
  getPopupCityListProperties: (state: SettingsState) =>
    state.siteData?.ru.popupCityList,
  getUpdatedTime: (state: AdaptedPageItem) => state.updatedAt,
  getCategoriesMenu: (state: SettingsState) =>
    getSiteDataObj(state, 'categoriesMenu'),
  getFloatingMenuButton: (state: SettingsState) =>
    getSiteDataObj(state, 'floatingMenuButton'),
  showFloatingMenuButton: (state: SettingsState) =>
    state.showFloatingMenuButton,
  showCategoriesMenu: (state: SettingsState) => state.showCategoriesMenu,
  getHeaderFullWidth: (state: SettingsState) => state.headerFullWidth,
  getHeaderVisuallyHidden: (state: SettingsState) => state.headerVisuallyHidden,
  showSearchBot: (state: SettingsState) => state.showSearchBot,
  getHeaderFooterNewDesign: (state: SettingsState) =>
    state.headerFooterNewDesign,
  getMainSurvey: (state: SettingsState) => state.mainSurvey,
  getTopMessage: (state: SettingsState) => state.topMessage,
  getIsProstoArticle: (state: SettingsState) => state.isProstoArticle,
}

export const mutations = {
  SET_HIDE_FOOTER(state: SettingsState, payload: boolean) {
    state.hideFooter = payload
  },
  SET_INIT_MAP(state: SettingsState, payload: boolean) {
    state.isMapInited = payload
  },
  SET_HIDE_HEADER(state: SettingsState, payload: boolean) {
    state.hideHeader = payload
  },
  SET_HIDE_SURVEY_BTN(state: SettingsState, payload: boolean) {
    state.hideSurveyBtn = payload
  },
  SET_SURVEY_BTN_NEW_DESIGN(state: SettingsState, payload: boolean) {
    state.surveyButtonNewDesign = payload
  },
  SET_CURRENT_LANG(state: SettingsState, payload: string) {
    state.curLang = payload
  },
  TOGGLE_SURVEY(state: SettingsState, payload: boolean) {
    state.showSurveyPopup = payload
  },
  PUSH_IFRAME_SRC(state: SettingsState, payload: string) {
    state.iframeSrcs.push(payload)
  },

  SET_IFRAME_SRC(state: SettingsState, payload: Array<string>) {
    state.iframeSrcs = payload
  },

  SET_SITE_DATA_WITH_CURLANG(state: SettingsState, { key, data }) {
    state.siteData[state.curLang] = {
      ...state.siteData[state.curLang],
      [key]: data,
    }
  },

  SET_THEME(state: SettingsState, theme: ThemeType) {
    state.theme = theme
  },

  SET_NOTIFICATION(
    state: SettingsState,
    notification: Notification | undefined
  ) {
    state.notification = notification
  },

  SET_INITIAL_STORE(state: SettingsState, payload) {
    state.siteData = {
      ...payload,
    }
  },

  SET_UPDATED_TIME(state: AdaptedPageItem, payload) {
    state.updatedAt = payload
  },

  SET_FEATURE_FLAGS(state: SettingsState, payload) {
    state.featureFlags = payload
  },

  SET_MOBILE_APP_BANNER(state: SettingsState, payload) {
    state.mobileAppBanner = payload
  },

  SET_HEADER_FOOTER_NEW_DESIGN(state: SettingsState, payload) {
    state.headerFooterNewDesign = payload
  },

  TOGGLE_FLOATING_MENU_BUTTON(state: SettingsState, payload: boolean) {
    state.showFloatingMenuButton = payload
  },

  SET_SHOW_CATEGORIES_MENU(state: SettingsState, payload: boolean) {
    state.showCategoriesMenu = payload
  },

  SET_HEADER_FULL_WIDTH(state: SettingsState, payload: boolean) {
    state.headerFullWidth = payload
  },

  SET_HEADER_VISUALLY_HIDDEN(state: SettingsState, payload: boolean) {
    state.headerVisuallyHidden = payload
  },

  SET_SHOW_SEARCH_BOT(state: SettingsState, payload: boolean) {
    state.showSearchBot = payload
  },

  SET_MAIN_SURVEY(state: SettingsState, payload) {
    state.mainSurvey = payload
  },

  SET_TOP_MESSAGE(state: SettingsState, payload) {
    state.topMessage = payload
  },

  SET_IS_PROSTO_ARTICLE(state: SettingsState, payload) {
    state.isProstoArticle = payload
  },
}

export const actions = {
  async getSettings({ commit }: { commit: any }, { $config, error, req }) {
    try {
      const res = await HTTP_REST_SERVER.get(
        $config.SM_API_GET_INITIAL_STATE_PATH
      )
      delete res?.data.redirects
      Object.keys(res?.data).forEach((lang) => {
        delete res?.data[lang].pageList
      })
      commit('SET_INITIAL_STORE', res?.data)
      const featureFlags = res?.data.FEATURE_FLAGS
      // eslint-disable-next-line
      if (featureFlags && !featureFlags['ff_redesigned_header']) {
        commit('registry/REGISTRY_COMPONENT', Registry.HeaderOld, {
          root: true,
        })
      }
      commit('SET_FEATURE_FLAGS', featureFlags)
    } catch (e) {
      error({ statusCode: e?.response?.status || 500 })
    }
  },
}
